import * as React from "react";
import { ConnectProto } from "@racoon/api";
import { Namespace, useT, useTypedT, type TranslationKey } from "@racoon/i18n";
import { TEXT_FIELD_MAX_LENGTH, fromTimestamp } from "@racoon/lib";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  TextArea,
  cn,
  useToast,
} from "@racoon/ui";
import { useGetDriverOnShiftByIdQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { useListRoutesQuery } from "~/shared/api/routes/route.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { Notice } from "~/shared/ui/notice";
import { RouteTransferModalContent } from "../msw-route-list/RouteTransferModal";
import { ForceCompleteRouteCascade } from "./force-complete-route-cascade";
import { useForceCompleteRun } from "~/features/force-complete-shift/model/use-force-complete-run";
import {
  confirmBlocker,
  unfinishedRoutes,
  type Decision,
  type DistributionMode,
  type JobResolution,
  type RouteResolution,
} from "~/features/force-complete-shift/lib/force-complete-shift";

const { DriverIncidentType } = ConnectProto;

/** The reasons on offer, in the order the design lists them. */
const REASONS = [
  DriverIncidentType.TRAFFIC_ACCIDENT,
  DriverIncidentType.TRUCK_BREAKDOWN,
  DriverIncidentType.DRIVER_UNWELL,
  DriverIncidentType.LOADERS_UNWELL,
  DriverIncidentType.OTHER,
];

const NO_ROUTES: ConnectProto.Route[] = [];

type Props = {
  shiftId: bigint;
  onClose: () => void;
  /** Fires once the backend has ended the shift, before the modal closes. */
  onCompleted?: () => void;
  /** True while the completion runs; the opener keeps the modal from being dismissed then. */
  onBusyChange?: (busy: boolean) => void;
};

/**
 * "Аварийное завершение смены" (Figma 21279:43447): the dispatcher names why the
 * shift ends and decides what becomes of its unfinished jobs; useForceCompleteRun
 * carries it out with the existing route and job RPCs, then ends the shift.
 */
export const ForceCompleteShiftModal: React.FC<Props> = ({
  shiftId,
  onClose,
  onCompleted,
  onBusyChange,
}) => {
  const t = useTypedT(Namespace.ForceCompleteShift);
  const tAny = useT();
  const { toast } = useToast();
  const { selectedCompanyId } = useSelectedCompany();
  const ownerOrganizationId = BigInt(selectedCompanyId ?? 0);
  const skip = !selectedCompanyId;

  const { data: shift, refetch: refetchShift } = useGetDriverOnShiftByIdQuery(
    { ownerOrganizationId, id: shiftId },
    { skip },
  );
  const {
    data: routesResponse,
    isLoading,
    isError,
    refetch,
  } = useListRoutesQuery(
    { ownerOrganizationIds: [ownerOrganizationId], driverOnShiftIds: [shiftId] },
    { skip },
  );
  const liveRoutes = routesResponse?.routes ?? NO_ROUTES;

  const [reason, setReason] = React.useState<ConnectProto.DriverIncidentType>();
  const [explanation, setExplanation] = React.useState("");
  const [mode, setMode] = React.useState<DistributionMode>("manual");
  const [resolutions, setResolutions] = React.useState<Map<bigint, JobResolution>>(() => new Map());
  const [routeResolutions, setRouteResolutions] = React.useState<Map<bigint, RouteResolution>>(
    () => new Map(),
  );
  const [target, setTarget] = React.useState<bigint | null>(null);
  const { run, progress } = useForceCompleteRun(shiftId);
  const running = progress != null;
  // Each call refetches the shift; the screen holds still on what was confirmed until the run ends.
  const [frozenRoutes, setFrozenRoutes] = React.useState<ConnectProto.Route[] | null>(null);
  const routes = frozenRoutes ?? liveRoutes;
  const [partlyApplied, setPartlyApplied] = React.useState(false);

  // Only "Другое" asks for an explanation; with any other reason none is sent.
  const isOther = reason === DriverIncidentType.OTHER;
  const decision: Decision = { reason, explanation: isOther ? explanation : "", mode, target };
  const blocker = confirmBlocker(decision);
  const pending = React.useMemo(() => unfinishedRoutes(routes), [routes]);
  const shiftDate = fromTimestamp(shift?.startedAt) ?? fromTimestamp(shift?.twStartAt) ?? undefined;

  // A mode switch starts the decision over; the reason is kept.
  const changeMode = (next: DistributionMode) => {
    setMode(next);
    setResolutions(new Map());
    setRouteResolutions(new Map());
    setTarget(null);
  };

  const resolve = (jobId: bigint, resolution: JobResolution) =>
    setResolutions((prev) => new Map(prev).set(jobId, resolution));
  const resolveRoute = (routeId: bigint, resolution: RouteResolution) =>
    setRouteResolutions((prev) => new Map(prev).set(routeId, resolution));

  const confirm = async () => {
    if (blocker || reason == null || running) return;
    setFrozenRoutes(routes);
    setPartlyApplied(false);
    onBusyChange?.(true);
    try {
      await run({ routes, resolutions, routeResolutions, decision: { ...decision, reason } });
      toast({ title: t("success") });
      onCompleted?.();
      onClose();
    } catch (error) {
      // The global error toaster names the cause. What ran stays done, so the
      // screen shows the shift as it is now and a retry continues from there.
      console.error("Force complete shift stopped", error);
      setPartlyApplied(true);
      await Promise.all([refetch(), refetchShift()]);
    } finally {
      setFrozenRoutes(null);
      onBusyChange?.(false);
    }
  };

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white"
      data-testid="force-complete-shift-modal"
    >
      <div className="grid shrink-0 grid-cols-2 gap-x-16 gap-y-4 p-6">
        <Field label={t("reason.label")}>
          <Select
            disabled={running}
            value={reason == null ? "" : String(reason)}
            onValueChange={(value) => setReason(Number(value) as ConnectProto.DriverIncidentType)}
          >
            <SelectTrigger className="h-8" data-testid="force-complete-shift-reason">
              <SelectValue placeholder={t("reason.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {REASONS.map((value) => {
                const name = DriverIncidentType[value];
                return (
                  <SelectItem
                    key={value}
                    value={String(value)}
                    data-testid={`force-complete-shift-reason-${name}`}
                  >
                    {tAny(
                      Namespace.Enum,
                      `DRIVER_INCIDENT_TYPE_${name}` as TranslationKey<Namespace.Enum>,
                    )}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t("mode.label")}>
          <Select
            disabled={running}
            value={mode}
            onValueChange={(value) => changeMode(value as DistributionMode)}
          >
            <SelectTrigger className="h-8" data-testid="force-complete-shift-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual" data-testid="force-complete-shift-mode-manual">
                {t("mode.manual")}
              </SelectItem>
              <SelectItem value="transfer" data-testid="force-complete-shift-mode-transfer">
                {t("mode.transfer")}
              </SelectItem>
            </SelectContent>
          </Select>
        </Field>

        {isOther && (
          <div className="col-span-2">
            <Field label={t("explanation.label")}>
              <TextArea
                data-testid="force-complete-shift-explanation"
                disabled={running}
                rows={2}
                maxLength={TEXT_FIELD_MAX_LENGTH}
                className="!min-h-0 resize-none"
                placeholder={t("explanation.placeholder")}
                value={explanation}
                onChange={(event) => setExplanation(event.target.value)}
              />
            </Field>
          </div>
        )}
      </div>

      <Separator />

      <div
        aria-busy={running}
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-auto transition-opacity duration-200",
          running && "pointer-events-none opacity-60",
        )}
      >
        {mode === "transfer" && shift ? (
          // The picker reads the shift's day and truck once, on mount, so it waits for them.
          <RouteTransferModalContent
            mode="shift"
            sourceShiftId={shiftId}
            sourceTruckId={shift.truckId}
            sourceRoutes={pending}
            initialDate={shiftDate}
            onClose={onClose}
            onTargetChange={setTarget}
          />
        ) : (
          <div className="px-6 pb-6">
            {isLoading || mode === "transfer" ? (
              <Notice>{t("loading")}</Notice>
            ) : isError ? (
              <Notice>{t("loadError")}</Notice>
            ) : pending.length === 0 ? (
              <Notice>{t("noUnfinishedRoutes")}</Notice>
            ) : (
              <ForceCompleteRouteCascade
                routes={routes}
                orgId={ownerOrganizationId}
                date={shiftDate}
                resolutions={resolutions}
                onResolutionChange={resolve}
                routeResolutions={routeResolutions}
                onRouteResolutionChange={resolveRoute}
              />
            )}
          </div>
        )}
      </div>

      <Separator />

      <div className="flex shrink-0 items-center justify-end gap-4 p-4">
        {partlyApplied && !running && (
          <span
            className="mr-auto text-xs text-text-warning"
            data-testid="force-complete-shift-partial"
          >
            {t("partlyApplied")}
          </span>
        )}
        {/* Only the explanation is called out; an empty select or an unpicked row speaks for itself. */}
        {blocker === "explanation" && (
          <span className="text-xs text-text-subtle" data-testid="force-complete-shift-blocker">
            {t("validation.explanation")}
          </span>
        )}
        <Button
          data-testid="force-complete-shift-confirm"
          disabled={blocker != null || running || isLoading || isError}
          onClick={confirm}
          variant="solidBlue"
          size="sm"
          // size="sm" brings text-xs and cn() does not merge, so the design's 14px is forced.
          className="!text-sm font-medium disabled:!bg-slate-200 disabled:text-slate-400"
        >
          {progress
            ? t("confirming", {
                done: Math.min(progress.done + 1, progress.total),
                total: progress.total,
              })
            : t(mode === "manual" ? "confirm.manual" : "confirm.transfer")}
        </Button>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium text-text-subtle">{label}</span>
    {children}
  </div>
);
